/**
 * PriorityProposalReviewPage
 *
 * Full review of a single PriorityProposal — the ranked-list governance
 * artifact created via "Lift to Proposal" on a completed session.
 *
 * Route: /prioritization/governance/proposals/:proposalId
 */

import { useEffect, useState } from 'react';
import { useParams, Link, useNavigate } from 'react-router-dom';
import {
  ArrowLeft,
  ScrollText,
  Layers,
  CheckCircle2,
  Star,
  ChevronRight,
  FileText,
  MessageSquare,
  Loader2,
  Award,
  AlertCircle,
} from 'lucide-react';
import { usePriorityProposalStore } from '@/stores/prioritization/priorityProposalStore';
import { useDegapProcessStore } from '@/stores/prioritization/degapProcessStore';
import { useUserStore } from '@/stores/prioritization/userStore';
import { useItemsStore } from '@/stores/prioritization/itemsStore';
import { PROPOSAL_STATUS_CONFIG } from '@/types/prioritization/priorityProposal';
import type { PriorityProposalStatus } from '@/types/prioritization/priorityProposal';
import { ItemRelationshipFlags } from '@/components/prioritization/ItemRelationshipFlags';
import { usePriorityListApproveRight } from '@/hooks/prioritization/useActRight';
import { useItemHref, useItemLinkScope } from '@/lib/itemLinkScope';

// ─── Status badge ─────────────────────────────────────────────────────────────

function StatusBadge({ status }: { status: PriorityProposalStatus }) {
  const config = PROPOSAL_STATUS_CONFIG[status];
  if (!config) return <span className="text-xs text-[var(--text-ter)]">{status}</span>;
  return (
    <span className={`inline-flex items-center px-2.5 py-1 rounded-full text-xs font-semibold ${config.badgeClass}`}>
      {config.label}
    </span>
  );
}

// ─── Pipeline advance config ──────────────────────────────────────────────────

const ADVANCE_CONFIG: Partial<Record<PriorityProposalStatus, {
  label: string;
  next: PriorityProposalStatus;
  color: string;
  isFinal?: boolean;
}>> = {
  catchball:    { label: 'Move to Under Review', next: 'under_review', color: 'bg-[var(--slate)] hover:bg-[var(--slate)]' },
  under_review: { label: 'Accept',               next: 'approved',     color: 'bg-[var(--slate)] hover:bg-[var(--slate)]' },
  approved:     { label: 'Ratify & Publish to Priority List', next: 'ratified', color: 'bg-[var(--green)] hover:bg-[var(--green)]', isFinal: true },
};

export default function PriorityProposalReviewPage() {
  const itemHref = useItemHref();
  const { proposalId } = useParams<{ proposalId: string }>();
  const navigate = useNavigate();

  const proposals = usePriorityProposalStore((s) => s.proposals);
  const fetchForCycle = usePriorityProposalStore((s) => s.fetchForCycle);
  const updateStatus = usePriorityProposalStore((s) => s.updateStatus);
  const ratifyAndPublish = usePriorityProposalStore((s) => s.ratifyAndPublish);
  const cycles = useDegapProcessStore((s) => s.cycles);
  const fetchItems = useItemsStore((s) => s.fetchFromSupabase);
  const userId = useUserStore((s) => s.userId) ?? 'system';

  const proposal = proposals.find((p) => p.id === proposalId);
  // 1017: the database's answer, as approve_priority_list (the final step) will judge it.
  const approveRight = usePriorityListApproveRight(proposal?.cycleId ?? null);
  const [loading, setLoading] = useState(!proposal);
  const [advancing, setAdvancing] = useState(false);
  const [publishResult, setPublishResult] = useState<{ success: boolean; approvedCount?: number; error?: string } | null>(null);

  // If not in local store yet, fetch all cycles' proposals as a fallback
  useEffect(() => {
    if (proposal) { setLoading(false); return; }
    const fetchAll = async () => {
      setLoading(true);
      await Promise.all(cycles.map((c) => fetchForCycle(c.id)));
      setLoading(false);
    };
    fetchAll();
  }, [proposal, cycles, fetchForCycle]);

  const cycle = cycles.find((c) => c.id === proposal?.cycleId);
  useItemLinkScope({ cycleId: proposal?.cycleId });

  const handleAdvance = async () => {
    if (!proposal) return;
    const cfg = ADVANCE_CONFIG[proposal.status];
    if (!cfg) return;
    setAdvancing(true);
    setPublishResult(null);
    try {
      if (cfg.isFinal) {
        // Ratify + call approve_priority_list RPC to stamp leadership_approved
        const result = await ratifyAndPublish(proposal.id, userId);
        setPublishResult(result);
        if (result.success) {
          // Refresh items so Priorities page reflects the change
          await fetchItems?.();
        }
      } else {
        // §2.3a: updateStatus now uses advance_proposal_status RPC
        const result = await updateStatus(proposal.id, cfg.next, userId);
        if (!result.success) {
          setPublishResult({ success: false, error: result.error });
        }
      }
    } finally {
      setAdvancing(false);
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center py-24">
        <Loader2 className="h-6 w-6 animate-spin text-[var(--text-ter)]" />
        <span className="ml-3 text-sm text-[var(--text-ter)]">Loading proposal…</span>
      </div>
    );
  }

  if (!proposal) {
    return (
      <div className="px-6 py-12 text-center">
        <FileText className="h-12 w-12 text-[var(--text-ter)] mx-auto mb-4" />
        <h2 className="text-lg font-semibold text-[var(--text-sec)] mb-2">Proposal not found</h2>
        <p className="text-sm text-[var(--text-ter)] mb-6">
          This proposal may have been removed or you may not have access to it.
        </p>
        <Link
          to="/prioritization/governance/proposals"
          className="inline-flex items-center gap-1.5 text-sm font-medium text-[var(--text-ter)] hover:text-[var(--text-ter)]"
        >
          <ArrowLeft className="h-4 w-4" />
          Back to Proposals
        </Link>
      </div>
    );
  }

  const advanceCfg = ADVANCE_CONFIG[proposal.status];

  return (
    <div className="max-w-3xl mx-auto px-4 py-8 space-y-6">

      {/* Back */}
      <div>
        <button
          type="button"
          onClick={() => navigate(-1)}
          className="inline-flex items-center gap-1.5 text-sm text-[var(--text-ter)] hover:text-[var(--text-pri)] transition-colors"
        >
          <ArrowLeft className="h-4 w-4" />
          Back
        </button>
      </div>

      {/* Publish result banner */}
      {publishResult && (
        publishResult.success ? (
          <div className="border border-[var(--green)] bg-[var(--green-dim)] px-5 py-4 flex items-start gap-3">
            <Award className="h-5 w-5 text-[var(--green)] flex-shrink-0 mt-0.5" />
            <div>
              <p className="text-sm font-semibold text-[var(--green)] dark:text-[var(--text-ter)]">
                Published to Priority List — {publishResult.approvedCount} item{publishResult.approvedCount !== 1 ? 's' : ''} approved
              </p>
              <p className="text-xs text-[var(--green)] dark:text-[var(--text-ter)] mt-0.5">
                Items are now marked as leadership-approved and shown in the cycle report.
              </p>
              <Link
                to={proposal?.cycleId ? `/prioritization/cycles/${proposal.cycleId}/report` : '/prioritization/reports/cycles'}
                className="inline-flex items-center gap-1 mt-2 text-xs font-medium text-[var(--green)] dark:text-[var(--text-ter)] underline hover:text-[var(--green)] dark:hover:text-[var(--text-ter)]"
              >
                View Priority List <ChevronRight className="h-3.5 w-3.5" />
              </Link>
            </div>
          </div>
        ) : (
          <div className="border border-[var(--coral)] bg-[var(--coral-dim)] px-5 py-4 flex items-start gap-3">
            <AlertCircle className="h-5 w-5 text-[var(--coral)] flex-shrink-0 mt-0.5" />
            <p className="text-sm text-[var(--coral)]">{publishResult.error}</p>
          </div>
        )
      )}

      {/* Header card */}
      <div className="bg-[var(--bg-raised)] border border-[var(--rule)] shadow-sm overflow-hidden">
        <div className="px-6 py-5 border-b border-[var(--rule)] flex items-start gap-4">
          <div className="w-10 h-10 bg-[var(--slate-dim)] flex items-center justify-center flex-shrink-0 mt-0.5">
            <ScrollText className="h-5 w-5 text-[var(--text-ter)]" />
          </div>
          <div className="flex-1 min-w-0">
            <p className="text-[11px] font-medium uppercase tracking-wider text-[var(--text-ter)] mb-0.5">
              Priority Proposal Review
            </p>
            <div className="flex items-center gap-2 flex-wrap mb-1">
              <h1 className="text-lg font-bold text-[var(--text-pri)]">{proposal.title}</h1>
              <StatusBadge status={proposal.status} />
            </div>
            <div className="flex items-center gap-4 text-xs text-[var(--text-ter)] flex-wrap">
              {cycle && (
                <span className="flex items-center gap-1">
                  <Layers className="h-3.5 w-3.5" />
                  {cycle.name}
                </span>
              )}
              <span>
                {proposal.rankedItems.length} item{proposal.rankedItems.length !== 1 ? 's' : ''}
                {' · '}
                {proposal.sessionIds.length} session{proposal.sessionIds.length !== 1 ? 's' : ''}
              </span>
              <span>Lifted {new Date(proposal.liftedAt).toLocaleDateString()}</span>
            </div>
          </div>

          {/* Advance / Ratify & Publish action — the final step is drawn only
              where the database will take it; anyone else reads its sentence (1017) */}
          {advanceCfg?.isFinal && !publishResult?.success && !approveRight.allowed && approveRight.line && (
            <p className="shrink-0 max-w-xs text-right text-sm text-[var(--text-ter)]">{approveRight.line}</p>
          )}
          {advanceCfg && !publishResult?.success && (!advanceCfg.isFinal || approveRight.allowed) && (
            <button
              type="button"
              onClick={handleAdvance}
              disabled={advancing}
              className={`shrink-0 inline-flex items-center gap-1.5 px-4 py-2 text-sm font-semibold text-white  disabled:opacity-40 transition-colors ${advanceCfg.color}`}
            >
              {advancing
                ? <Loader2 className="h-4 w-4 animate-spin" />
                : advanceCfg.isFinal
                  ? <Award className="h-4 w-4" />
                  : <CheckCircle2 className="h-4 w-4" />}
              {advanceCfg.label}
            </button>
          )}

          {/* Published state */}
          {proposal.status === 'ratified' && !advanceCfg && (
            <Link
              to={proposal?.cycleId ? `/prioritization/cycles/${proposal.cycleId}/report` : '/prioritization/reports/cycles'}
              className="shrink-0 inline-flex items-center gap-1.5 px-4 py-2 text-sm font-medium text-[var(--green)] dark:text-[var(--text-ter)] bg-[var(--green-dim)] border border-[var(--green)] hover:bg-[var(--green-dim)] transition-colors"
            >
              <Award className="h-4 w-4" />
              View on Priority List
            </Link>
          )}
        </div>

        {/* Rationale */}
        {proposal.rationale && (
          <div className="px-6 py-4 border-b border-[var(--rule)]">
            <h2 className="text-xs font-semibold text-[var(--text-ter)] uppercase tracking-wider mb-2">Rationale</h2>
            <p className="text-sm text-[var(--text-sec)] leading-relaxed">{proposal.rationale}</p>
          </div>
        )}

        {/* Open questions */}
        {proposal.openQuestions && (
          <div className="px-6 py-4 border-b border-[var(--rule)]">
            <h2 className="text-xs font-semibold text-[var(--text-ter)] uppercase tracking-wider mb-2 flex items-center gap-1.5">
              <MessageSquare className="h-3.5 w-3.5" />
              Open Questions for Catchball
            </h2>
            <p className="text-sm text-[var(--text-sec)] leading-relaxed">{proposal.openQuestions}</p>
          </div>
        )}
      </div>

      {/* Ranked items */}
      <div className="bg-[var(--bg-raised)] border border-[var(--rule)] shadow-sm overflow-hidden">
        <div className="px-6 py-4 border-b border-[var(--rule)] flex items-center justify-between">
          <h2 className="text-sm font-semibold text-[var(--text-sec)]">Ranked Priority List</h2>
          <span className="text-xs text-[var(--text-ter)]">{proposal.rankedItems.length} items</span>
        </div>
        {proposal.rankedItems.length === 0 ? (
          <div className="px-6 py-8 text-center text-sm text-[var(--text-ter)]">
            No items in this proposal.
          </div>
        ) : (
          <ul className="divide-y divide-[var(--rule)]">
            {proposal.rankedItems.map((item) => (
              <li key={item.itemId} className="flex items-center gap-3 px-6 py-3">
                <span className="w-7 text-center text-sm font-bold text-[var(--text-ter)] flex-shrink-0">
                  {item.rank}
                </span>
                <div className="flex-1 min-w-0">
                  <div className="flex items-center gap-1.5 flex-wrap">
                    {item.itemId ? (
                      <Link
                        to={itemHref(item.itemId)}
                        className="text-sm font-medium text-[var(--text-pri)] hover:text-[var(--text-ter)] hover:underline truncate transition-colors"
                      >
                        {item.itemTitle}
                      </Link>
                    ) : (
                      <span className="text-sm font-medium text-[var(--text-pri)] truncate">{item.itemTitle}</span>
                    )}
                    <ItemRelationshipFlags itemId={item.itemId} />
                    {item.isStrategicallyNominated && (
                      <Star className="h-3.5 w-3.5 fill-amber-400 stroke-amber-500 flex-shrink-0" />
                    )}
                    {item.category && (
                      <span className="px-1.5 py-0.5 text-xs rounded bg-[var(--hover-tint)] text-[var(--text-sec)]">
                        {item.category}
                      </span>
                    )}
                  </div>
                </div>
                {item.score !== undefined && (
                  <span className="text-xs font-mono text-[var(--text-ter)] flex-shrink-0">
                    {item.score.toFixed(1)}
                  </span>
                )}
                {item.itemId && (
                  <Link
                    to={itemHref(item.itemId)}
                    className="text-[var(--text-ter)] hover:text-[var(--text-ter)] transition-colors flex-shrink-0"
                    title="Open item detail"
                  >
                    <ChevronRight className="h-4 w-4" />
                  </Link>
                )}
              </li>
            ))}
          </ul>
        )}
      </div>

    </div>
  );
}
